/*
 * FactFindTest - Mobile-first fact find prototype mounted at /fact-find-test.
 *
 * This is a STANDALONE visual prototype:
 * - No Redux, no Firebase, no auth.
 * - Pure local React state (class component, no hooks).
 * - Google Places autocomplete is STUBBED (see primitives/AddressAutocomplete.js).
 *
 * Field model mirrors the legacy fact find so when wiring goes live later we
 * can map this shape onto the existing data contract.
 */
import React from 'react';
import { Helmet } from 'react-helmet';
import history from '../../history';
import Shell from './Shell';
import { buildStepList, findStepIndex, parseStepId } from './steps.config';
import { validateStep, scrollToFirstError } from './validation';
import StepGeneralDetails from './steps/StepGeneralDetails';
import StepAddressHistory from './steps/StepAddressHistory';
import StepEmployment from './steps/StepEmployment';
import StepLivingExpenses from './steps/StepLivingExpenses';
import StepLiabilities from './steps/StepLiabilities';
import StepAssets from './steps/StepAssets';
import StepLoanRequirements from './steps/StepLoanRequirements';
import DevApplicantModal, { DevToolsButton } from './DevApplicantModal';
import {
  fetchFactFindV2,
  saveFactFindV2Section,
  submitFactFindV2,
} from './api';

let APPLICANT_COUNTER = 0;
const makeApplicantId = () => {
  APPLICANT_COUNTER += 1;
  if (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID) {
    try {
      return window.crypto.randomUUID();
    } catch (e) {
      // fall through
    }
  }
  return `applicant_${Date.now()}_${APPLICANT_COUNTER}`;
};

let DEPENDENT_COUNTER = 0;
export const makeDependentId = () => {
  DEPENDENT_COUNTER += 1;
  if (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID) {
    try {
      return window.crypto.randomUUID();
    } catch (e) {
      // fall through
    }
  }
  return `dep_${Date.now()}_${DEPENDENT_COUNTER}`;
};

const buildEmptyApplicant = () => ({
  id: makeApplicantId(),
  title: '',
  given_name: '',
  middle_name: '',
  family_name: '',
  maiden_name: '',
  home_phone: '',
  mobile: '',
  work_phone: '',
  email: '',
  dob: { day: '', month: '', year: '' },
  gender: '',
  martial_status: '',
  resident_status: '',
  resident_visa_details: '',
  // Each dependent: { id, age, sharedWith: [applicantId, ...] }
  // sharedWith does NOT include the owning applicant.
  dependants: [],
  other_income_details: '',
  address: {
    current: {
      // `address_search` is the raw text in the autocomplete search box.
      // The structured pieces below are only filled when a suggestion is
      // picked (or hand-edited), so typing into the search box never
      // smashes the parsed fields together.
      address_search: '',
      // ISO 3166-1 alpha-2 country code (lower case). Defaults to AU.
      country: 'au',
      unit_number: '',
      street_number: '',
      street_name: '',
      // `street` is kept as a back-compat formatted string for any consumer
      // that still expects it; it's regenerated alongside the split fields.
      street: '',
      suburb: '',
      state: '',
      postcode: '',
      address_type: '',
      address_amount: '',
      other_text: '',
      moved_in: { day: '', month: '', year: '' },
    },
    previous: [],
  },
  employment: {
    current: {
      position: '',
      employer_name: '',
      employer_phone: '',
      employer_address: '',
      gross_income: '',
      employment_status: '',
      start_date: { day: '', month: '', year: '' },
    },
    previous: [],
  },
});

const buildEmptyExpenses = () => ({
  groceries: '',
  clothing: '',
  transport: '',
  communication: '',
  recreation: '',
  child_care: '',
  pet_care: '',
  health: '',
  health_insurance: '',
  general_insurance: '',
  sickness: '',
  public_school: '',
  private_school: '',
  higher_edu: '',
  primary_res_running_costs: '',
  primary_res_strata: '',
  investment_running: '',
  secondary_res_running: '',
  other: '',
  other_amount: '',
});

const buildEmptyLiabilities = () => ({
  loans: {},
  credit_cards: {},
});

const buildEmptyAssets = () => ({
  properties: {},
  home_contents_value: '',
  savings: {},
  shares: {},
  supers: {},
  vehicles: {},
  other_assets: {},
});

const buildEmptyLoanRequirements = () => ({
  why: {
    wide_choice: false,
    expert_advice: false,
    convienence: false,
    legwork: false,
    existing_customer: false,
  },
  what_can_assist: {
    purchase: false,
    refinance: false,
    build_new_home: false,
    existing_loan: false,
  },
  property: '',
  payment_plan: {
    repayment_prior_to_retirement: false,
    downsizing: false,
    sale_of_assets: false,
    super_income: false,
    super_lump_sum: false,
    savings: false,
    other_investments: false,
    co_app_income: false,
    other_check: false,
    other: '',
  },
  loan_term: '',
  loan_term_other: '',
  changes_in_financial_circum: '',
  changes_in_financial_circum_yes: '',
  loan_repayment_type: '',
  interest_only_term: '',
  rate_type: '',
  important_features: {
    // Each feature stores an answer of '', 'Yes', 'NiceToHave' or 'No'.
    basic_home_loan: { answer: '' },
    offset_account: { answer: '' },
    redraw_facility: { answer: '' },
    professional_packages: { answer: '' },
    extra_repayments_allowed: { answer: '' },
  },
  important_features_other: '',
});

// Map between the React state field names (camelCase) and the section
// strings the backend expects (lower_snake on data.<section>).
const SECTION_FOR_STATE = {
  applicants: 'applicants',
  expenses: 'expenses',
  liabilities: 'liabilities',
  assets: 'assets',
  loanRequirements: 'loan_requirements',
};

class FactFindTest extends React.Component {
  // True when the route mounted with an :applicationId param, i.e. the
  // customer is hitting /fact-find/:id. In that mode every change autosaves
  // to the backend. /fact-find-test (no param) stays an in-memory sandbox.
  hasBackend = () => {
    const m = this.props && this.props.match && this.props.match.params;
    return !!(m && m.applicationId);
  };

  applicationId = () => {
    const m = this.props && this.props.match && this.props.match.params;
    return m ? m.applicationId : null;
  };

  constructor(props) {
    super(props);
    // The broker pre-creates applicants when setting up the application,
    // so the customer-facing form never adds or removes them. For the
    // /fact-find-test sandbox we seed two so the per-applicant nav is
    // visible. For /fact-find/:id the real data is fetched in componentDidMount.
    const applicant1 = buildEmptyApplicant();
    const applicant2 = buildEmptyApplicant();
    this.state = {
      // Personal step IDs encode the applicant: "General_Details:<id>".
      // See steps.config.js for the full step model.
      currentStep: `General_Details:${applicant1.id}`,
      applicants: [applicant1, applicant2],
      expenses: buildEmptyExpenses(),
      liabilities: buildEmptyLiabilities(),
      assets: buildEmptyAssets(),
      loanRequirements: buildEmptyLoanRequirements(),
      submitted: false,
      submitSuccess: false,
      submitting: false,
      devToolsOpen: false,
      submitModalOpen: false,
      // Step IDs the user has attempted to advance from. Once a step is
      // marked attempted, that step's errors are shown live until they're
      // fixed.
      attemptedSteps: {},

      // Backend wiring state. `bootstrapping` blocks the UI until the
      // initial fetch resolves on /fact-find/:id. Autosave still runs in
      // the background but we don't surface a status chip - the form silently
      // syncs each section as the user edits.
      bootstrapping: false,
      bootstrapError: '',
    };

    // Debounce timers keyed by section so multiple sections can have
    // independent pending saves in flight.
    this.saveTimers = {};
    // Track in-flight save promises so we don't overlap requests for the
    // same section.
    this.pendingSaves = {};
  }

  componentDidMount() {
    if (this.hasBackend()) {
      this.bootstrapFromBackend();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.currentStep !== this.state.currentStep) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  componentWillUnmount() {
    // Flush any pending debounced saves so a navigate-away doesn't drop them.
    Object.keys(this.saveTimers).forEach((section) => {
      if (this.saveTimers[section]) {
        clearTimeout(this.saveTimers[section]);
        this.flushSave(section);
      }
    });
  }

  bootstrapFromBackend = async () => {
    const applicationId = this.applicationId();
    if (!applicationId) return;
    this.setState({ bootstrapping: true, bootstrapError: '' });
    try {
      const res = await fetchFactFindV2(applicationId);
      if (!res || !res.ok) {
        throw new Error('fetch failed');
      }
      const data = res.data || {};
      // Backend stores applicants array directly; ensure shape for safety.
      const applicants = Array.isArray(data.applicants) && data.applicants.length
        ? data.applicants
        : [buildEmptyApplicant()];
      const expenses = data.expenses && Object.keys(data.expenses).length
        ? { ...buildEmptyExpenses(), ...data.expenses }
        : buildEmptyExpenses();
      const liabilities = data.liabilities && Object.keys(data.liabilities).length
        ? { ...buildEmptyLiabilities(), ...data.liabilities }
        : buildEmptyLiabilities();
      const assets = data.assets && Object.keys(data.assets).length
        ? { ...buildEmptyAssets(), ...data.assets }
        : buildEmptyAssets();
      const loanRequirements = data.loan_requirements && Object.keys(data.loan_requirements).length
        ? { ...buildEmptyLoanRequirements(), ...data.loan_requirements }
        : buildEmptyLoanRequirements();

      this.setState({
        bootstrapping: false,
        applicants,
        expenses,
        liabilities,
        assets,
        loanRequirements,
        currentStep: `General_Details:${applicants[0].id}`,
      });
    } catch (err) {
      console.log('[FactFindTest] bootstrap failed', err);
      this.setState({
        bootstrapping: false,
        bootstrapError:
          'We couldn\'t load your fact find. Refresh the page or contact your broker.',
      });
    }
  };

  // Schedule a debounced save for a given section. Called by every mutator.
  queueSave = (stateField) => {
    if (!this.hasBackend()) return;
    const section = SECTION_FOR_STATE[stateField];
    if (!section) return;

    if (this.saveTimers[section]) clearTimeout(this.saveTimers[section]);
    this.saveTimers[section] = setTimeout(() => {
      this.saveTimers[section] = null;
      this.flushSave(section);
    }, 500);
  };

  // Send the current value of `section` to the backend immediately.
  flushSave = async (section) => {
    const applicationId = this.applicationId();
    if (!applicationId) return;

    // Resolve the matching slice from state for this backend section.
    const stateField = Object.keys(SECTION_FOR_STATE).find(
      (k) => SECTION_FOR_STATE[k] === section
    );
    if (!stateField) return;
    const payload = this.state[stateField];

    try {
      // Only allow one in-flight save per section; chain any newer one
      // after it so we never trample.
      const previous = this.pendingSaves[section] || Promise.resolve();
      const next = previous.then(() =>
        saveFactFindV2Section(applicationId, section, payload)
      );
      this.pendingSaves[section] = next;
      await next;
      this.pendingSaves[section] = null;
    } catch (err) {
      console.log('[FactFindTest] save failed', section, err);
      this.pendingSaves[section] = null;
    }
  };

  // ===== Step navigation =====
  steps = () => buildStepList(this.state.applicants);

  goToStep = (stepId) => {
    // Forward jumps (sidebar clicks past the active step) are only allowed
    // when the current step has no validation errors. Backward / same-step
    // navigation is always allowed so the user can review or fix earlier
    // sections. Continue button already validates, so this is a no-op for it.
    const steps = this.steps();
    const currentIdx = findStepIndex(steps, this.state.currentStep);
    const targetIdx = findStepIndex(steps, stepId);
    if (targetIdx > currentIdx) {
      const result = validateStep(this.state.currentStep, this.state);
      if (result.hasErrors) {
        this.setState(
          (s) => ({
            attemptedSteps: { ...s.attemptedSteps, [s.currentStep]: true },
          }),
          () => {
            setTimeout(() => scrollToFirstError(result.errors), 50);
          }
        );
        return;
      }
    }
    this.setState({ currentStep: stepId, submitted: false });
  };

  goNext = () => {
    const steps = this.steps();
    const idx = findStepIndex(steps, this.state.currentStep);
    const next = steps[idx + 1];
    if (!next) return;

    // Run validation for the current step. Block advance if invalid, mark
    // the step as attempted (so errors are shown live) and scroll the user
    // to the first error.
    const result = validateStep(this.state.currentStep, this.state);
    if (result.hasErrors) {
      this.setState(
        (s) => ({
          attemptedSteps: { ...s.attemptedSteps, [s.currentStep]: true },
        }),
        () => {
          // Defer so the error styles render before we scroll.
          setTimeout(() => scrollToFirstError(result.errors), 50);
        }
      );
      return;
    }
    this.goToStep(next.id);
  };

  goPrev = () => {
    const steps = this.steps();
    const idx = findStepIndex(steps, this.state.currentStep);
    const prev = steps[idx - 1];
    // Previous always allowed - never block going back.
    if (prev) this.goToStep(prev.id);
  };

  updateApplicant = (id, patch) => {
    this.setState(
      (s) => ({
        applicants: s.applicants.map((a) => (a.id === id ? { ...a, ...patch } : a)),
      }),
      () => this.queueSave('applicants')
    );
  };

  // ===== Dev-only applicant management =====
  openDevTools = () => this.setState({ devToolsOpen: true });
  closeDevTools = () => this.setState({ devToolsOpen: false });

  addApplicant = () => {
    const next = buildEmptyApplicant();
    this.setState(
      (s) => ({ applicants: [...s.applicants, next] }),
      () => this.queueSave('applicants')
    );
  };

  removeApplicant = (id) => {
    this.setState(
      (s) => {
        if (s.applicants.length <= 1) return null;
        const remaining = s.applicants.filter((a) => a.id !== id);

        // Strip ownership references and dependent shared-with references
        // that pointed at the removed applicant.
        const stripOwnership = (collection) => {
          const out = {};
          Object.keys(collection || {}).forEach((k) => {
            const item = collection[k];
            const ownership = { ...(item.ownership || {}) };
            delete ownership[id];
            out[k] = { ...item, ownership };
          });
          return out;
        };

        const sanitizedApplicants = remaining.map((a) => ({
          ...a,
          dependants: (a.dependants || []).map((d) => ({
            ...d,
            sharedWith: (d.sharedWith || []).filter((depId) => depId !== id),
          })),
        }));

        // If the removed applicant owned the current step, jump to that
        // section for the first remaining applicant so the user isn't stranded.
        let currentStep = s.currentStep;
        const { kind, applicantId } = parseStepId(currentStep);
        if (applicantId === id) {
          const fallbackId = remaining[0] && remaining[0].id;
          currentStep = fallbackId ? `${kind}:${fallbackId}` : 'Loan_Requirements';
        }

        return {
          applicants: sanitizedApplicants,
          currentStep,
          liabilities: {
            loans: stripOwnership(s.liabilities.loans),
            credit_cards: stripOwnership(s.liabilities.credit_cards),
          },
          assets: {
            ...s.assets,
            properties: stripOwnership(s.assets.properties),
            savings: stripOwnership(s.assets.savings),
            shares: stripOwnership(s.assets.shares),
            supers: stripOwnership(s.assets.supers),
            vehicles: stripOwnership(s.assets.vehicles),
            other_assets: stripOwnership(s.assets.other_assets),
          },
        };
      },
      () => {
        this.queueSave('applicants');
        this.queueSave('liabilities');
        this.queueSave('assets');
      }
    );
  };

  renameApplicant = (id, field, value) => {
    this.setState((s) => ({
      applicants: s.applicants.map((a) =>
        a.id === id ? { ...a, [field]: value } : a
      ),
    }));
  };

  // ===== Joint sections =====
  updateExpenses = (patch) => {
    this.setState(
      (s) => ({ expenses: { ...s.expenses, ...patch } }),
      () => this.queueSave('expenses')
    );
  };

  updateLiabilities = (patch) => {
    this.setState(
      (s) => ({ liabilities: { ...s.liabilities, ...patch } }),
      () => this.queueSave('liabilities')
    );
  };

  // Removing a loan also needs to strip its id from any property's
  // `linked_loan_ids`, so we do it in a single setState here rather than
  // splitting the responsibility between sections.
  removeLoan = (loanId) => {
    this.setState(
      (s) => {
        const nextLoans = { ...(s.liabilities.loans || {}) };
        delete nextLoans[loanId];

        const properties = { ...(s.assets.properties || {}) };
        Object.keys(properties).forEach((k) => {
          const p = properties[k];
          const linked = Array.isArray(p.linked_loan_ids) ? p.linked_loan_ids : [];
          if (linked.includes(loanId)) {
            properties[k] = {
              ...p,
              linked_loan_ids: linked.filter((id) => id !== loanId),
            };
          }
        });

        return {
          liabilities: { ...s.liabilities, loans: nextLoans },
          assets: { ...s.assets, properties },
        };
      },
      () => {
        this.queueSave('liabilities');
        this.queueSave('assets');
      }
    );
  };

  updateAssets = (patch) => {
    this.setState(
      (s) => ({ assets: { ...s.assets, ...patch } }),
      () => this.queueSave('assets')
    );
  };

  updateLoanRequirements = (patch) => {
    this.setState(
      (s) => ({ loanRequirements: { ...s.loanRequirements, ...patch } }),
      () => this.queueSave('loanRequirements')
    );
  };

  // ===== Submit (test only) =====
  // The footer button on the final step opens this modal, which then calls
  // handleSubmit to "send" the form (logs to console + toast).
  openSubmitModal = () => this.setState({ submitModalOpen: true });
  closeSubmitModal = () => this.setState({ submitModalOpen: false });

  handleSubmit = async () => {
    // Guard against double submission. Without this, a double-click (or an
    // impatient second click while the flush + submit request is in flight)
    // hits /submit twice: the first call marks the form completed and fires a
    // "completed their fact find" event, the second sees it already completed
    // and fires a "re-submitted their fact find" event - two notifications for
    // one action.
    //
    // The guard is a synchronous instance flag rather than this.state.submitting
    // because setState is async: two clicks fired in the same tick both read the
    // old state (false) before React commits the update, so a state-only guard
    // still lets both requests through. The flag flips immediately. (The server
    // /submit route also dedups submit events as a backstop.)
    if (this._submitInFlight) return;
    this._submitInFlight = true;
    this.setState({ submitting: true });

    // In sandbox mode just log + toast like before.
    if (!this.hasBackend()) {
      /* eslint-disable no-console */
      console.log('[FactFindTest] test submission (no backend)', {
        applicants: this.state.applicants,
        expenses: this.state.expenses,
        liabilities: this.state.liabilities,
        assets: this.state.assets,
        loanRequirements: this.state.loanRequirements,
      });
      /* eslint-enable no-console */
      this._submitInFlight = false;
      this.setState({ submitted: true, submitModalOpen: false, submitting: false });
      setTimeout(() => this.setState({ submitted: false }), 4000);
      return;
    }

    // Real submit: make sure any debounced section save is flushed first,
    // then hit /submit which snapshots a version + marks completed.
    const applicationId = this.applicationId();
    const sectionsToFlush = Object.keys(SECTION_FOR_STATE).map(
      (k) => SECTION_FOR_STATE[k]
    );
    try {
      await Promise.all(
        sectionsToFlush.map((section) => {
          if (this.saveTimers[section]) {
            clearTimeout(this.saveTimers[section]);
            this.saveTimers[section] = null;
            return this.flushSave(section);
          }
          return Promise.resolve();
        })
      );
      await submitFactFindV2(applicationId);
      // Don't navigate yet - swap the modal into a "what's next?" view so
      // the user can jump straight into VOI / docs / bank statements.
      this._submitInFlight = false;
      this.setState({
        submitted: true,
        submitSuccess: true,
        submitting: false,
      });
    } catch (err) {
      console.log('[FactFindTest] submit failed', err);
      // Re-enable the button so the user can retry after a failure.
      this._submitInFlight = false;
      this.setState({ submitting: false });
    }
  };

  // Close the success modal and head back to the customer dashboard. Used
  // as a fallback when the customer doesn't pick one of the next-step
  // buttons.
  goToDashboard = () => {
    this.setState({ submitSuccess: false, submitModalOpen: false });
    history.push('/dashboard');
  };

  // Navigate to a specific post-submit task (VOI, docs, bank statements).
  goToTask = (path) => {
    this.setState({ submitSuccess: false, submitModalOpen: false });
    history.push(path);
  };

  // Footer "Submit" button: validate then open the confirmation modal.
  attemptSubmit = () => {
    const result = validateStep(this.state.currentStep, this.state);
    if (result.hasErrors) {
      this.setState(
        (s) => ({
          attemptedSteps: { ...s.attemptedSteps, [s.currentStep]: true },
        }),
        () => {
          setTimeout(() => scrollToFirstError(result.errors), 50);
        }
      );
      return;
    }
    this.openSubmitModal();
  };

  // ===== Render step content =====
  renderStep() {
    const {
      currentStep,
      applicants,
      expenses,
      liabilities,
      assets,
      loanRequirements,
      attemptedSteps,
    } = this.state;
    const { kind, applicantId } = parseStepId(currentStep);
    // Fall back to the first applicant if a personal step's applicant ID
    // doesn't match (shouldn't happen, but keeps the UI safe).
    const safeApplicantId =
      applicantId && applicants.some((a) => a.id === applicantId)
        ? applicantId
        : applicants[0] && applicants[0].id;

    // Validation results for the active step. Errors are only surfaced after
    // the user has attempted to advance past this step.
    const validation = validateStep(currentStep, this.state);
    const attempted = !!attemptedSteps[currentStep];
    const stepErrors = attempted ? validation.errors : {};

    switch (kind) {
      case 'General_Details':
        return (
          <StepGeneralDetails
            applicants={applicants}
            applicantId={safeApplicantId}
            onUpdateApplicant={this.updateApplicant}
            errors={stepErrors}
          />
        );
      case 'Address_History':
        return (
          <StepAddressHistory
            applicants={applicants}
            applicantId={safeApplicantId}
            onUpdateApplicant={this.updateApplicant}
            errors={stepErrors}
          />
        );
      case 'Employment':
        return (
          <StepEmployment
            applicants={applicants}
            applicantId={safeApplicantId}
            onUpdateApplicant={this.updateApplicant}
            errors={stepErrors}
          />
        );
      case 'Living_Expenses':
        return (
          <StepLivingExpenses
            expenses={expenses}
            onUpdateExpenses={this.updateExpenses}
            errors={stepErrors}
          />
        );
      case 'Liabilities':
        return (
          <StepLiabilities
            liabilities={liabilities}
            applicants={applicants}
            properties={assets.properties || {}}
            onUpdateLiabilities={this.updateLiabilities}
            onRemoveLoan={this.removeLoan}
            errors={stepErrors}
          />
        );
      case 'Assets':
        return (
          <StepAssets
            assets={assets}
            applicants={applicants}
            loans={liabilities.loans || {}}
            onUpdateAssets={this.updateAssets}
            onJumpToLiabilities={() => this.goToStep('Liabilities')}
            errors={stepErrors}
          />
        );
      case 'Loan_Requirements':
        return (
          <StepLoanRequirements
            loanRequirements={loanRequirements}
            onUpdateLoanRequirements={this.updateLoanRequirements}
            errors={stepErrors}
          />
        );
      default:
        return null;
    }
  }

  renderFooterNav() {
    const steps = this.steps();
    const idx = findStepIndex(steps, this.state.currentStep);
    const isFirst = idx <= 0;
    const isLast = idx >= steps.length - 1;

    const attempted = !!this.state.attemptedSteps[this.state.currentStep];
    const validation = validateStep(this.state.currentStep, this.state);
    const errorCount = Object.keys(validation.errors).length;
    const showErrorBanner = attempted && errorCount > 0;

    return (
      <div className="mt-6 space-y-3">
        {showErrorBanner ? (
          <div
            className="flex items-center gap-3 rounded-xl border-l-4 px-4 py-3"
            style={{ backgroundColor: '#FEE2E2', borderColor: '#DC2626' }}
          >
            <svg
              className="h-5 w-5 flex-shrink-0"
              viewBox="0 0 20 20"
              fill="#DC2626"
            >
              <path
                fillRule="evenodd"
                d="M8.3 2.7a2 2 0 013.4 0l6.5 11A2 2 0 0116.5 17h-13a2 2 0 01-1.7-3.3l6.5-11zM10 7a1 1 0 011 1v3a1 1 0 11-2 0V8a1 1 0 011-1zm0 8a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#991B1B]">
                {errorCount === 1
                  ? '1 field still needs your attention.'
                  : `${errorCount} fields still need your attention.`}
              </p>
              <p className="text-xs text-[#991B1B] mt-0.5">
                Fix the highlighted fields above to continue.
              </p>
            </div>
            <button
              type="button"
              onClick={() => scrollToFirstError(validation.errors)}
              className="text-xs font-semibold text-[#991B1B] underline whitespace-nowrap"
            >
              Jump to first
            </button>
          </div>
        ) : null}

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between sm:items-center">
          <button
            type="button"
            onClick={this.goPrev}
            disabled={isFirst}
            className="rounded-xl border-2 bg-white px-5 py-3 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ borderColor: '#D1D5DB', color: '#1F2937' }}
          >
            Previous
          </button>
          {isLast ? (
            <button
              type="button"
              onClick={this.attemptSubmit}
              className="rounded-xl px-6 py-3 text-sm font-semibold text-white shadow-md transition-transform active:scale-95 inline-flex items-center gap-2"
              style={{ backgroundColor: '#7C5CFC' }}
            >
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 011.4-1.4l3.8 3.8 6.8-6.8a1 1 0 011.4 0z"
                  clipRule="evenodd"
                />
              </svg>
              Submit Fact Find
            </button>
          ) : (
            <button
              type="button"
              onClick={this.goNext}
              className="rounded-xl px-6 py-3 text-sm font-semibold text-white shadow-md transition-transform active:scale-95"
              style={{ backgroundColor: '#7C5CFC' }}
            >
              Continue →
            </button>
          )}
        </div>
      </div>
    );
  }

  renderSubmitModal() {
    if (!this.state.submitModalOpen && !this.state.submitSuccess) return null;
    const isSuccess = this.state.submitSuccess;

    // Picking a backdrop click target depends on which stage we're in.
    // Pre-submit: close the modal. Post-submit success: head to the
    // customer dashboard.
    const handleBackdrop = (e) => {
      if (e.target !== e.currentTarget) return;
      if (isSuccess) this.goToDashboard();
      else this.closeSubmitModal();
    };

    return (
      <div
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6"
        style={{ backgroundColor: 'rgba(15, 17, 26, 0.55)', backdropFilter: 'blur(2px)' }}
        role="dialog"
        aria-modal="true"
        onClick={handleBackdrop}
      >
        <div
          className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-xl"
          style={{ border: '1px solid #E5E7EB' }}
        >
          {isSuccess
            ? this.renderSubmitSuccessBody()
            : this.renderSubmitConfirmBody()}
        </div>
      </div>
    );
  }

  renderSubmitConfirmBody() {
    return (
      <>
        <div className="px-5 sm:px-6 py-5 border-b border-[#E5E7EB]">
          <div
            className="inline-flex items-center justify-center h-10 w-10 rounded-full mb-3"
            style={{ backgroundColor: '#EFEAFE', color: '#5B3FE0' }}
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M2.94 5.45a1 1 0 011.42 0L8 9.09l5.65-5.65a1 1 0 011.4 1.42L8.7 11.2a1 1 0 01-1.4 0L2.94 6.87a1 1 0 010-1.42z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-[#1F2937]">
            Ready to submit your fact find?
          </h2>
          <p className="mt-1 text-sm text-[#6B7280]">
            Your fact find will be sent to your broker. You can come back at any time
            to update your information, nothing here is locked in. We won't email you
            a copy of these details.
          </p>
        </div>

        <div className="px-5 sm:px-6 py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end rounded-b-2xl">
          <button
            type="button"
            onClick={this.closeSubmitModal}
            className="rounded-xl border-2 bg-white px-4 py-2.5 text-sm font-semibold"
            style={{ borderColor: '#D1D5DB', color: '#1F2937' }}
          >
            Not yet, take me back
          </button>
          <button
            type="button"
            onClick={this.handleSubmit}
            disabled={this.state.submitting}
            className="rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#7C5CFC' }}
          >
            {this.state.submitting ? 'Submitting…' : 'Yes, submit'}
          </button>
        </div>
      </>
    );
  }

  // Post-submit "what's next?" view. Lists the remaining portal tasks as
  // tappable cards so the customer can hop straight into VOI / docs /
  // bank statements. If they pick nothing, the "Back to dashboard"
  // button (or the backdrop) sends them home.
  renderSubmitSuccessBody() {
    const applicationId = this.applicationId();
    const tasks = [
      {
        key: 'id',
        title: 'Verification of Identity',
        description: 'Verify your identity to process your application.',
        path: applicationId ? `/identification/${applicationId}` : null,
        icon: (
          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 2l7 3v5c0 4-3 7-7 8-4-1-7-4-7-8V5l7-3z" />
          </svg>
        ),
      },
      {
        key: 'docs',
        title: 'File Upload',
        description: 'Upload the supporting documents we need.',
        path: applicationId ? `/upload/${applicationId}` : null,
        icon: (
          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 3a1 1 0 011 1v8.6l2.3-2.3a1 1 0 011.4 1.4l-4 4a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4L9 12.6V4a1 1 0 011-1zM4 16a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1z" />
          </svg>
        ),
      },
      {
        key: 'bank',
        title: 'Bank Statements',
        description: 'Securely share your bank statements.',
        path: applicationId ? `/bankstatements/${applicationId}` : null,
        icon: (
          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path d="M3 7l7-4 7 4v2H3V7zm0 4h14v2H3v-2zm0 4h14v2H3v-2z" />
          </svg>
        ),
      },
    ];

    return (
      <>
        <div className="px-5 sm:px-6 py-5 border-b border-[#E5E7EB]">
          <div
            className="inline-flex items-center justify-center h-10 w-10 rounded-full mb-3"
            style={{ backgroundColor: '#DCFCE7', color: '#166534' }}
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 011.4-1.4l3.8 3.8 6.8-6.8a1 1 0 011.4 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-[#1F2937]">
            Fact find submitted
          </h2>
          <p className="mt-1 text-sm text-[#6B7280]">
            Your broker has been notified. Here's what to tackle next, jump
            straight in or head back to your dashboard.
          </p>
        </div>

        <div className="px-5 sm:px-6 py-4 bg-[#F9FAFB] space-y-2">
          {tasks.map((t) => (
            <button
              key={t.key}
              type="button"
              disabled={!t.path}
              onClick={() => t.path && this.goToTask(t.path)}
              className="w-full flex items-center gap-3 rounded-xl bg-white border border-[#E5E7EB] px-3 py-3 text-left transition-all hover:border-[#C4B5FD] hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span
                className="inline-flex items-center justify-center h-10 w-10 rounded-lg flex-shrink-0"
                style={{ backgroundColor: '#EFEAFE', color: '#5B3FE0' }}
              >
                {t.icon}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#1F2937]">
                  {t.title}
                </p>
                <p className="text-xs text-[#6B7280] truncate">
                  {t.description}
                </p>
              </div>
              <svg
                className="h-4 w-4 flex-shrink-0"
                viewBox="0 0 20 20"
                fill="currentColor"
                style={{ color: '#9CA3AF' }}
              >
                <path
                  fillRule="evenodd"
                  d="M7.3 4.3a1 1 0 011.4 0l5 5a1 1 0 010 1.4l-5 5a1 1 0 01-1.4-1.4L11.6 10 7.3 5.7a1 1 0 010-1.4z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          ))}
        </div>

        <div className="px-5 sm:px-6 py-4 flex justify-end rounded-b-2xl">
          <button
            type="button"
            onClick={this.goToDashboard}
            className="rounded-xl border-2 bg-white px-4 py-2.5 text-sm font-semibold"
            style={{ borderColor: '#D1D5DB', color: '#1F2937' }}
          >
            Back to dashboard
          </button>
        </div>
      </>
    );
  }

  renderToast() {
    // Only show the toast in sandbox (no backend) mode. Backend submits
    // navigate the user straight back to /dashboard.
    if (!this.state.submitted || this.hasBackend()) return null;
    const message = 'Test submission logged - no data was saved.';
    return (
      <div className="dui-toast dui-toast-end z-50 fixed bottom-4 right-4">
        <div
          className="dui-alert rounded-xl px-4 py-3 shadow-md"
          style={{ backgroundColor: '#16A34A', color: '#FFFFFF' }}
        >
          <span className="text-sm font-semibold">{message}</span>
        </div>
      </div>
    );
  }

  renderBootstrapLoader() {
    return (
      <div
        className="min-h-screen w-full flex items-center justify-center"
        style={{ backgroundColor: '#F5F6FA' }}
      >
        <div className="rounded-2xl bg-white px-6 py-5 shadow-sm border border-[#E5E7EB] text-center max-w-sm w-full mx-4">
          <div
            className="inline-flex items-center justify-center h-10 w-10 rounded-full mb-3"
            style={{ backgroundColor: '#EFEAFE' }}
          >
            <span
              className="inline-block h-3 w-3 rounded-full animate-pulse"
              style={{ backgroundColor: '#7C5CFC' }}
            />
          </div>
          <h2 className="text-base font-bold text-[#1F2937]">
            Loading your fact find...
          </h2>
          <p className="mt-1 text-sm text-[#6B7280]">
            One moment while we pull up your application.
          </p>
        </div>
      </div>
    );
  }

  renderBootstrapError() {
    return (
      <div
        className="min-h-screen w-full flex items-center justify-center"
        style={{ backgroundColor: '#F5F6FA' }}
      >
        <div className="rounded-2xl bg-white px-6 py-5 shadow-sm border border-[#E5E7EB] text-center max-w-sm w-full mx-4">
          <div
            className="inline-flex items-center justify-center h-10 w-10 rounded-full mb-3"
            style={{ backgroundColor: '#FEE2E2' }}
          >
            <svg className="h-5 w-5 text-[#DC2626]" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M8.3 2.7a2 2 0 013.4 0l6.5 11A2 2 0 0116.5 17h-13a2 2 0 01-1.7-3.3l6.5-11zM10 7a1 1 0 011 1v3a1 1 0 11-2 0V8a1 1 0 011-1zm0 8a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <h2 className="text-base font-bold text-[#1F2937]">
            Couldn't load your fact find
          </h2>
          <p className="mt-1 text-sm text-[#6B7280]">{this.state.bootstrapError}</p>
          <button
            type="button"
            onClick={this.bootstrapFromBackend}
            className="mt-4 rounded-lg px-4 py-2 text-sm font-semibold text-white"
            style={{ backgroundColor: '#7C5CFC' }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  render() {
    const helmet = (
      <Helmet>
        {/*
          Toggle the modern-page body styles via <html className>. See
          client/src/index.css ".lg-modern-page" rules - they override the
          legacy /css/loader.css (which forces html/body to 100px), hide
          the full-screen .full-loader overlay, and set the modern
          background + font. Using <html> attributes (instead of an
          inline <style> tag) ensures react-helmet@6.1 correctly REMOVES
          them on unmount so they don't leak into /admin pages (which
          would otherwise lose Poppins font + Metronic's flex layout).
        */}
        <html className="lg-modern-page" />
        <title>Fact Find - LoanGateway</title>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
      </Helmet>
    );

    // Loading / error states block the form during the initial backend fetch.
    if (this.state.bootstrapping) {
      return (
        <div>
          {helmet}
          {this.renderBootstrapLoader()}
        </div>
      );
    }
    if (this.state.bootstrapError) {
      return (
        <div>
          {helmet}
          {this.renderBootstrapError()}
        </div>
      );
    }

    return (
      <Shell
        applicants={this.state.applicants}
        currentStepId={this.state.currentStep}
        onJump={this.goToStep}
        // When mounted at /fact-find/:applicationId, expose the id to Shell
        // so the sidebar / mobile header can render a "Back to dashboard"
        // link. In sandbox mode (/fact-find-test) the link is hidden.
        applicationId={this.applicationId()}
      >
        {helmet}
        {this.renderStep()}
        {this.renderFooterNav()}

        {/* Dev applicant tools are only useful in the sandbox. In production
            (when an applicationId is present in the URL) the broker manages
            applicants from the admin side. */}
        {!this.hasBackend() ? (
          <>
            <DevToolsButton
              applicantCount={this.state.applicants.length}
              onOpen={this.openDevTools}
            />
            <DevApplicantModal
              open={this.state.devToolsOpen}
              onClose={this.closeDevTools}
              applicants={this.state.applicants}
              onAdd={this.addApplicant}
              onRemove={this.removeApplicant}
              onRename={this.renameApplicant}
            />
          </>
        ) : null}

        {this.renderSubmitModal()}
        {this.renderToast()}
      </Shell>
    );
  }
}

export default FactFindTest;
